/**
 * Third-party script optimizer to reduce Total Blocking Time (TBT)
 * and improve Core Web Vitals
 */

// Utility to defer script loading until user interaction or idle time
const deferScriptLoading = (src, id, options = {}) => {
  if (typeof window === 'undefined') return;
  
  // Check if script already exists
  if (document.getElementById(id)) return;
  
  const {
    async = true,
    defer = true,
    onLoad = null,
    onError = null,
    attributes = {},
    loadStrategy = 'idle' // 'idle', 'interaction', 'visible'
  } = options;

  const loadScript = () => {
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = async;
    script.defer = defer;
    
    // Add custom attributes
    Object.entries(attributes).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });
    
    if (onLoad) script.onload = onLoad;
    if (onError) script.onerror = onError;
    
    document.head.appendChild(script);
  };

  // Different loading strategies
  switch (loadStrategy) {
    case 'idle':
      if ('requestIdleCallback' in window) {
        requestIdleCallback(loadScript, { timeout: 7000 });
      } else {
        setTimeout(loadScript, 7000);
      }
      break;
      
    case 'interaction':
      const events = ['mousedown', 'touchstart', 'keydown', 'scroll'];
      const loadOnInteraction = () => {
        loadScript();
        events.forEach(event => 
          document.removeEventListener(event, loadOnInteraction, { passive: true })
        );
      };
      events.forEach(event => 
        document.addEventListener(event, loadOnInteraction, { passive: true })
      );
      break;
      
    case 'visible':
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              loadScript();
              observer.disconnect();
            }
          });
        });
        // Observe the first form or CTA element
        const target = document.querySelector('form, .cta, [data-load-script]');
        if (target) observer.observe(target);
      } else {
        loadScript();
      }
      break;
      
    default:
      loadScript();
  }
};

// Optimized GTM loading
const loadGTMOptimized = (gtmId) => {
  if (typeof window === 'undefined') return;

  // Initialize dataLayer first (synchronous, minimal impact)
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });

  // Load GTM script with idle strategy
  deferScriptLoading(
    `https://www.googletagmanager.com/gtm.js?id=GTM-${gtmId}`,
    'gtm-script',
    {
      loadStrategy: 'idle',
      async: true,
      onLoad: () => {
        console.log('GTM loaded successfully on user interaction');
      }
    }
  );
};

// Optimized Revenue Hero loading
let revenueHeroLoader = null;

const ensureRevenueHeroLoaded = (loadStrategy = 'idle') => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.RevenueHero) return Promise.resolve(window.RevenueHero);
  if (revenueHeroLoader) return revenueHeroLoader;

  revenueHeroLoader = new Promise((resolve, reject) => {
    deferScriptLoading(
      'https://app.revenuehero.io/scheduler.min.js',
      'revenue-hero-script',
      {
        loadStrategy,
        onLoad: () => resolve(window.RevenueHero),
        onError: (err) => reject(err || new Error('RevenueHero failed to load'))
      }
    );
  });

  return revenueHeroLoader;
};

const loadRevenueHeroOptimized = () => {
  const targetFormIds = [
    '517ebc0a-e3b2-48e7-994d-19507b711e00',
    'c169c509-7422-4098-bdd2-085a67d82092',
    'e23ab1ed-02c7-42c1-90bc-ecd78ae786c3',
    '3ba0360d-0fb0-4473-84c9-a7362657431c',
    '568567fd-2abb-43a4-b030-a6e2d7875387'
  ];

  const handleFormSubmitEvent = (event) => {
    // HubSpot may send event.data as an object or JSON string; normalize it
    let payload = event.data;
    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch (err) {
        return;
      }
    }

    if (payload?.type === 'hsFormCallback' && payload.eventName === 'onFormSubmitted') {
      const formId = payload.id;
      if (!targetFormIds.includes(formId)) return;

      const formValues = payload.data?.submissionValues || {};

      ensureRevenueHeroLoaded('immediate')
        .then((RevenueHero) => {
          if (!RevenueHero) return;
          const hero = new RevenueHero({ routerId: '1226' });
          return hero.submit(formValues).then((sessionData) => {
            hero.dialog.open(sessionData);
          });
        })
        .catch((err) => console.warn('RevenueHero load failed:', err));
    }
  };

  // Listen immediately so we don't miss the submission event even if the script isn't ready yet
  window.addEventListener('message', handleFormSubmitEvent, false);

  // Preload the script once the page is interactive/idle to minimize chances of missing submissions
  ensureRevenueHeroLoaded('idle').catch((err) => console.warn('RevenueHero preload failed:', err));

  // Also force a direct load shortly after hydration to cover cases where idle/interactions are blocked
  setTimeout(() => {
    ensureRevenueHeroLoaded('immediate').catch(() => {});
  }, 3000);
};

// Optimized HubSpot form loading
const loadHubSpotOptimized = () => {
  // Only load HubSpot scripts when forms are visible
  const hubspotForms = document.querySelectorAll('[data-portal-id], .hs-form, .hubspot-form');
  
  if (hubspotForms.length === 0) return;

  deferScriptLoading(
    'https://js.hsforms.net/forms/embed/v2.js',
    'hubspot-forms-script',
    {
      loadStrategy: 'interaction',
      onLoad: () => {
        console.log('HubSpot forms script loaded');
      }
    }
  );
};

// Critical resource hints for better performance
const addResourceHints = () => {
  if (typeof window === 'undefined') return;
  
  const hints = [
    // DNS prefetch for external domains
    { rel: 'dns-prefetch', href: '//fonts.googleapis.com' },
    { rel: 'dns-prefetch', href: '//www.googletagmanager.com' },
    { rel: 'dns-prefetch', href: '//js.hsforms.net' },
    { rel: 'dns-prefetch', href: '//app.revenuehero.io' },
    { rel: 'dns-prefetch', href: '//geolocation-db.com' },
    
    // Preconnect for critical resources
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: 'anonymous' },
  ];
  
  hints.forEach(hint => {
    const existing = document.querySelector(`link[href="${hint.href}"]`);
    if (existing) return;
    
    const link = document.createElement('link');
    Object.entries(hint).forEach(([key, value]) => {
      if (key === 'crossorigin') {
        link.crossOrigin = value;
      } else {
        link[key] = value;
      }
    });
    document.head.appendChild(link);
  });
};

// Initialize performance optimizations
const initPerformanceOptimizations = () => {
  if (typeof window === 'undefined') return;
  
  // Add resource hints immediately
  addResourceHints();
  
  // Wait for page to be interactive before loading heavy scripts
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => {
        loadHubSpotOptimized();
        loadRevenueHeroOptimized();
      }, 1000);
    });
  } else {
    setTimeout(() => {
      loadHubSpotOptimized();
      loadRevenueHeroOptimized();
    }, 1000);
  }
};

// Optimized country check with caching
const checkCountryOptimized = async () => {
  // Check cache first
  const cachedCountry = sessionStorage.getItem('user_country');
  const cacheTime = sessionStorage.getItem('country_cache_time');
  const now = Date.now();
  
  // Cache for 1 hour
  if (cachedCountry && cacheTime && (now - parseInt(cacheTime)) < 3600000) {
    return cachedCountry;
  }
  
  try {
    // Use a faster, CDN-backed service
    const response = await fetch('https://ipapi.co/json/', {
      headers: { 'Accept': 'application/json' }
    });
    
    if (!response.ok) throw new Error('Network response was not ok');
    
    const data = await response.json();
    const country = data.country_name || 'Unknown';
    
    // Cache the result
    sessionStorage.setItem('user_country', country);
    sessionStorage.setItem('country_cache_time', now.toString());
    
    return country;
  } catch (error) {
    console.warn('Country detection failed:', error);
    return cachedCountry || 'Unknown';
  }
};

// CommonJS exports
module.exports = {
  deferScriptLoading,
  loadGTMOptimized,
  loadRevenueHeroOptimized,
  loadHubSpotOptimized,
  addResourceHints,
  initPerformanceOptimizations,
  checkCountryOptimized
};
